import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNav, useSubscriptionRenewalGuard } from '@hero/web-hooks';
import { AlertCircleIcon, RefreshCcwIcon } from '@hero/web-icons';
import { cn } from '@hero/web-lib';

/**
 * Self-contained renewal-nudge pill mounted in the carrier-dashboard
 * header. Reads the current `Subscription` via
 * `useSubscriptionRenewalGuard` and renders nothing when the sub is on
 * free tier, canceled, paused, or still inside its period. Clicking
 * navigates to `/billing` so the user can pay the most recent unpaid
 * Billing.
 *
 * This is the **interim UX** while Moyasar tokenization (Option 4) is
 * deferred — see
 * [docs/adr/001-moyasar-tokenization-deferred.md](docs/adr/001-moyasar-tokenization-deferred.md).
 * Once subscriptions auto-renew silently, this badge can be removed.
 *
 * Single visual primitive: `severity` (`'soft' | 'hard'`) from the
 * guard drives the colour tokens (amber vs red). Copy is picked based
 * on `reason` so trial-end, period-end, and past-due-grace each get
 * accurate wording.
 */
export function SubscriptionRenewalBadge() {
  const { t } = useTranslation('m-billing');
  const { navigate } = useNav();
  const guard = useSubscriptionRenewalGuard();

  if (!guard.shouldRenew) return null;

  const isHard = guard.severity === 'hard';

  // No countdown — by design. See `useSubscriptionRenewalGuard` for
  // the "countdowns invite procrastination" rationale.
  const label = (() => {
    if (guard.reason === 'past_due_overdue') return t('renewal_badge.hard_title');
    if (guard.reason === 'trial_ended') return t('renewal_badge.trial_ended');
    return t('renewal_badge.soft_title');
  })();

  return (
    <button
      type="button"
      onClick={() => navigate('billing')}
      aria-label={label}
      className={cn(
        'inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold transition-colors focus:outline-none focus:ring-2 focus:ring-offset-1',
        isHard
          ? 'border-red-200 bg-red-50 text-red-800 hover:bg-red-100 focus:ring-red-300'
          : 'border-amber-200 bg-amber-50 text-amber-800 hover:bg-amber-100 focus:ring-amber-300',
      )}>
      {isHard ? (
        <AlertCircleIcon className="!h-3.5 !w-3.5" />
      ) : (
        <RefreshCcwIcon className="!h-3.5 !w-3.5" />
      )}
      <span>{label}</span>
    </button>
  );
}
